import { Input } from '@thanx/uikit/input'
import { Radio } from '@thanx/uikit/radio'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import Section from 'components/Section'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import CmsPage from 'scenes/Cms/components/CmsPage'

import useDispatch from 'hooks/useDispatch'
import { setReplyAddress, setReplyMode } from 'slices/orderFeedbackReplyAddress'
import {
  selectAddressError,
  selectReplyAddress,
  selectReplyMode,
} from 'slices/orderFeedbackReplyAddress/selectors'
import { ReplyMode } from 'slices/orderFeedbackReplyAddress/types'
import { useOwningFeedbackToolName } from './hooks/useOwningFeedbackToolName'
import { useReplyAddressFormInitialize } from './hooks/useReplyAddressFormInitialize'
import { useSettingsEditPermission } from './hooks/useSettingsEditPermission'
import { useWrittenFeedbackPersisted } from './hooks/useWrittenFeedbackEnabled'
import { PANE_TITLE_STYLE } from './paneTitleStyle'
import WrittenFeedbackGateNote from './WrittenFeedbackGateNote'

const t = buildTranslate('order_ratings.settings')

const GATE_NOTE_ID = 'reply-address-gate-note'
const THIRD_PARTY_NOTE_ID = 'reply-address-third-party-note'
const ADDRESS_ERROR_ID = 'reply-address-error'
const ADDRESS_INPUT_ID = 'reply-address-input'
const MODE_GROUP_NAME = 'reply-address-mode'

// Measured off prototype v67's own computed styles (R1 sec 1.3): a 4px-radius
// bordered card with tight 6px/24px padding, each radio row supplying its own
// 22px vertical padding, separated by a hairline divider.
const CARD_BORDER_COLOR = palette.grey30
const CARD_RADIUS = '4px'
const CARD_PADDING = '6px 24px'
const ROW_PADDING = '22px 0'
const DIVIDER_COLOR = palette.grey10
const OPTION_FONT_SIZE = '15px'
const OPTION_LINE_HEIGHT = '22.5px'
const INPUT_MAX_WIDTH = '360px'

// FR-55. A merchant chooses whether a guest's reply to their order-feedback
// message goes to the dashboard user who sent it, or to one fixed address.
//
// Built from scenes/Cms/Settings/EmailReplyRouting/ReplySection.tsx: standalone
// `Radio` (not uikit's `RadioGroup`, which clones every child as a radio and
// would leak radio props onto the dependent address input), with the address
// field sitting directly under its own option, same as that file.
const ReplyAddressPane = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const inputRef = useRef<HTMLInputElement>(null)
  // Set only by handleModeChange below, and cleared once consumed. NOT a
  // "did replyMode just change to fixed" check — that would also fire (and
  // wrongly steal focus) when useReplyAddressFormInitialize's own resetForm
  // loads a merchant who is already saved in fixed mode.
  const focusPending = useRef(false)

  useReplyAddressFormInitialize()

  const replyMode = useSelector(selectReplyMode)
  const replyAddress = useSelector(selectReplyAddress)
  const addressError = useSelector(selectAddressError)

  // The PERSISTED value, not the live one: measured on prototype v70, the
  // dependent panes are untouched while the Feedback collection toggle is
  // merely staged, and gate only once Publish lands.
  const writtenFeedbackEnabled = useWrittenFeedbackPersisted()
  const owningToolName = useOwningFeedbackToolName()
  const { editDisabled } = useSettingsEditPermission()

  useEffect(() => {
    if (focusPending.current && replyMode === 'fixed') {
      focusPending.current = false
      inputRef.current?.focus()
    }
  }, [replyMode])

  // R1 sec 1.4: choosing "A single address" both enables the input and moves
  // focus into it in the same click — a merchant doesn't need a second click
  // to start typing. The focus itself waits for the effect above because the
  // input is still `disabled` (and so unfocusable) until this render commits.
  const handleModeChange = useCallback(
    (mode: ReplyMode) => {
      dispatch(setReplyMode(mode))
      if (mode === 'fixed') focusPending.current = true
    },
    [dispatch]
  )

  const handleAddressChange = useCallback(
    (value: string) => dispatch(setReplyAddress(value)),
    [dispatch]
  )

  // Real disabling (R1 sec 0.3's gap, deliberately not copied here): the
  // prototype only fades a wrapper with opacity + pointer-events, which
  // leaves every control keyboard-reachable and submittable. RBAC's reason is
  // already stated by the pane-wide EditPermissionBanner above the sub-nav and
  // the gate's by the note above the card, so no control restates either.
  //
  // A third-party tool owns feedback only while written feedback is off, so it
  // never gates on its own; it only swaps the note, because a merchant whose
  // guests never see the Thanx form does not need to be told to turn written
  // feedback on. Defensive: v70 hides the Settings tab for that merchant, so
  // the tool's note covers a deep link or an integration connected mid-session.
  const gated = !writtenFeedbackEnabled
  const controlsDisabled = editDisabled || gated
  const gateNoteId = owningToolName ? THIRD_PARTY_NOTE_ID : GATE_NOTE_ID

  const optionLabelClass = css({
    fontSize: OPTION_FONT_SIZE,
    fontWeight: 700,
    lineHeight: OPTION_LINE_HEIGHT,
    color: palette.grey90,
  })

  return (
    <CmsPage
      title={t('reply_address.title')}
      titleClassName={css(PANE_TITLE_STYLE)}
      description={t('reply_address.subtitle')}
    >
      {gated && (
        <div className="mb-m">
          <WrittenFeedbackGateNote id={gateNoteId}>
            {owningToolName ? (
              <>
                {t('reply_address.third_party_gate_note.prefix')}{' '}
                <b>{owningToolName}</b>
                {t('reply_address.third_party_gate_note.suffix')}
              </>
            ) : (
              <>
                {t('reply_address.gate_note.prefix')}{' '}
                <b>{t('written_feedback_gate.phrase')}</b>{' '}
                {t('reply_address.gate_note.suffix')}
              </>
            )}
          </WrittenFeedbackGateNote>
        </div>
      )}
      <Section
        className="mb-m"
        style={{
          opacity: controlsDisabled ? 0.5 : 1,
          borderColor: CARD_BORDER_COLOR,
          borderRadius: CARD_RADIUS,
          padding: CARD_PADDING,
        }}
      >
        <div
          role="group"
          aria-label={t('reply_address.title')}
          aria-describedby={gated ? gateNoteId : undefined}
        >
          <div className={css({ padding: ROW_PADDING })}>
            <Radio
              name={MODE_GROUP_NAME}
              value="user"
              checked={replyMode === 'user'}
              disabled={controlsDisabled}
              onChange={() => handleModeChange('user')}
            >
              <span className={optionLabelClass}>
                {t('reply_address.options.user')}
              </span>
            </Radio>
          </div>
          <div
            className={css({ height: '1px', backgroundColor: DIVIDER_COLOR })}
          />
          <div className={css({ padding: ROW_PADDING })}>
            <Radio
              name={MODE_GROUP_NAME}
              value="fixed"
              checked={replyMode === 'fixed'}
              disabled={controlsDisabled}
              onChange={() => handleModeChange('fixed')}
            >
              <span className={optionLabelClass}>
                {t('reply_address.options.single')}
              </span>
            </Radio>
            <Input
              type="email"
              value={replyAddress}
              onChange={handleAddressChange}
              disabled={controlsDisabled || replyMode !== 'fixed'}
              errored={!!addressError}
              inputRef={inputRef}
              placeholder={t('reply_address.address_placeholder')}
              className={css({
                maxWidth: INPUT_MAX_WIDTH,
                marginTop: '12px',
              })}
              overrides={{
                Input: {
                  props: {
                    id: ADDRESS_INPUT_ID,
                    'aria-label': t('reply_address.address_aria_label'),
                    'aria-required': replyMode === 'fixed',
                    'aria-invalid': !!addressError,
                    'aria-describedby': ADDRESS_ERROR_ID,
                  },
                },
              }}
            />
            <div id={ADDRESS_ERROR_ID} role="alert">
              {addressError && (
                <Text.BodyText5 tag="div" color="cayenne50" className="mt-xxs">
                  {t(`reply_address.address_error.${addressError}`)}
                </Text.BodyText5>
              )}
            </div>
          </div>
        </div>
      </Section>
    </CmsPage>
  )
}

export default ReplyAddressPane
